import { useEffect, useSyncExternalStore } from "react";

export type ThemePref = "light" | "dark" | "system";
const KEY = "hx-theme";

// Light unless the person picks Dark or Match system in Settings.
function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === "dark" || v === "system" ? v : "light";
  } catch {
    return "light";
  }
}

let pref: ThemePref = readPref();
const listeners = new Set<() => void>();
const media = typeof window !== "undefined" ? window.matchMedia("(prefers-color-scheme: dark)") : null;

function resolved(p: ThemePref): "light" | "dark" {
  if (p === "system") return media?.matches ? "dark" : "light";
  return p;
}

function apply() {
  const t = resolved(pref);
  document.documentElement.dataset.theme = t;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", t === "dark" ? "#0C0E12" : "#EFF1F4");
}

media?.addEventListener("change", () => {
  if (pref === "system") {
    apply();
    listeners.forEach((l) => l());
  }
});

export function setThemePref(p: ThemePref) {
  pref = p;
  try {
    localStorage.setItem(KEY, p);
  } catch {
    /* storage can be unavailable; the choice still applies for this visit */
  }
  apply();
  listeners.forEach((l) => l());
}

export function useTheme() {
  const value = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => `${pref}:${resolved(pref)}`,
  );
  const [p, r] = value.split(":") as [ThemePref, "light" | "dark"];
  useEffect(() => apply(), []);
  return { pref: p, resolved: r, setPref: setThemePref };
}
